export type WidgetTranslator = (
  key: string,
  options?: Record<string, unknown>,
) => string;

const AUDIT_MODULE_KEYS: Record<string, string> = {
  attendance: 'attendance',
  me: 'me',
  onboarding: 'onboarding',
  organization: 'organization',
  processes: 'processes',
  system: 'system',
  integrations: 'integrations',
  public_api: 'publicApi',
};

const AUDIT_ACTION_KEYS: Record<string, string> = {
  create: 'create',
  update: 'update',
  delete: 'delete',
  invite: 'invite',
  deactivate: 'deactivate',
  terminate: 'terminate',
  patch: 'patch',
  event: 'event',
};

const AUDIT_RESOURCE_KEYS: Record<string, string> = {
  worker_groups: 'workerGroups',
  punch: 'punch',
  workers: 'workers',
  branches: 'branches',
  profile: 'profile',
  devices: 'devices',
  connections: 'connections',
  leave_request: 'leaveRequest',
  worker_device: 'workerDevice',
  auth: 'auth',
  record: 'record',
};

const PRIORITY_KEYS: Record<string, string> = {
  urgent: 'urgent',
  high: 'high',
  medium: 'medium',
  low: 'low',
};

function normalizeCode(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/[\s/.-]+/g, '_');
}

export function formatDashboardDate(
  value: string | Date | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, options).format(date);
}

export function formatDashboardTime(
  value: string | Date | null | undefined,
  locale: string,
): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatRelativeTime(
  iso: string | null | undefined,
  t: WidgetTranslator,
  locale: string,
  now = Date.now(),
): string {
  if (!iso) return '—';
  const timestamp = Date.parse(iso);
  if (!Number.isFinite(timestamp)) return '—';
  const elapsed = Math.max(0, now - timestamp);
  const minutes = elapsed / 60_000;
  if (minutes < 1) return t('dashboard:widgets.shared.relativeTime.justNow');
  if (minutes < 60) {
    return t('dashboard:widgets.shared.relativeTime.minutesAgo', { count: Math.max(1, Math.round(minutes)) });
  }
  const hours = minutes / 60;
  if (hours < 24) {
    return t('dashboard:widgets.shared.relativeTime.hoursAgo', { count: Math.round(hours) });
  }
  const days = hours / 24;
  if (days < 2) return t('dashboard:widgets.shared.relativeTime.yesterday');
  if (days < 7) {
    return t('dashboard:widgets.shared.relativeTime.daysAgo', { count: Math.round(days) });
  }
  return formatDashboardDate(new Date(timestamp), locale);
}

export function translateAuditModule(t: WidgetTranslator, value: string | null | undefined): string {
  const key = AUDIT_MODULE_KEYS[normalizeCode(value)];
  return key
    ? t(`dashboard:widgets.recentAuditLogs.modules.${key}`)
    : t('dashboard:widgets.recentAuditLogs.genericModule');
}

export function translateAuditAction(t: WidgetTranslator, value: string | null | undefined): string {
  const key = AUDIT_ACTION_KEYS[normalizeCode(value)] ?? 'event';
  return t(`dashboard:widgets.recentAuditLogs.actions.${key}`);
}

export function translateAuditResource(t: WidgetTranslator, value: string | null | undefined): string {
  const key = AUDIT_RESOURCE_KEYS[normalizeCode(value)] ?? 'record';
  return t(`dashboard:widgets.recentAuditLogs.resources.${key}`);
}

export function translatePriority(t: WidgetTranslator, value: string | null | undefined): string {
  const key = PRIORITY_KEYS[normalizeCode(value)] ?? 'normal';
  return t(`dashboard:widgets.shared.priorities.${key}`);
}
